import React, { PropsWithChildren } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useCreateIndeedFlexShiftMutation } from 'queries/admin/indeedFlex/useCreateIndeedFlexShiftMutation'
import { useCreateIndeedFlexPastShiftMutation } from 'queries/admin/indeedFlex/useCreateIndeedFlexPastShiftMutation'
import { useCreateIndeedFlexLongTermAssignmentMutation } from 'queries/admin/indeedFlex/useCreateIndeedFlexLongTermAssignmentMutation'

type IndeedFlexShiftActionsContextState = {
  state: {
    createIndeedFlexShiftMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    createIndeedFlexPastShiftMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    createIndeedFlexLongTermAssignmentMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    createIndeedFlexShift: ReturnType<
      typeof useCreateIndeedFlexShiftMutation
    >['mutate']
    resetCreateIndeedFlexShift: ReturnType<
      typeof useCreateIndeedFlexShiftMutation
    >['reset']
    createIndeedFlexPastShift: ReturnType<
      typeof useCreateIndeedFlexPastShiftMutation
    >['mutate']
    resetCreateIndeedFlexPastShift: ReturnType<
      typeof useCreateIndeedFlexPastShiftMutation
    >['reset']
    createIndeedFlexLongTermAssignment: ReturnType<
      typeof useCreateIndeedFlexLongTermAssignmentMutation
    >['mutate']
    resetCreateIndeedFlexLongTermAssignment: ReturnType<
      typeof useCreateIndeedFlexLongTermAssignmentMutation
    >['reset']
  }
}

const [Context, useIndeedFlexShiftActionsContextValue] =
  createContext<IndeedFlexShiftActionsContextState>({
    name: 'IndeedFlexShiftActionsContext',
  })

export const IndeedFlexShiftActionsProvider = ({
  children,
}: PropsWithChildren) => {
  const {
    mutate: createIndeedFlexShift,
    isPending: isCreateIndeedFlexShiftPending,
    isError: isCreateIndeedFlexShiftError,
    isSuccess: isCreateIndeedFlexShiftSuccess,
    error: createIndeedFlexShiftError,
    reset: resetCreateIndeedFlexShift,
  } = useCreateIndeedFlexShiftMutation()

  const {
    mutate: createIndeedFlexPastShift,
    isPending: isCreateIndeedFlexPastShiftPending,
    isError: isCreateIndeedFlexPastShiftError,
    isSuccess: isCreateIndeedFlexPastShiftSuccess,
    error: createIndeedFlexPastShiftError,
    reset: resetCreateIndeedFlexPastShift,
  } = useCreateIndeedFlexPastShiftMutation()

  const {
    mutate: createIndeedFlexLongTermAssignment,
    isPending: isCreateIndeedFlexLongTermAssignmentPending,
    isError: isCreateIndeedFlexLongTermAssignmentError,
    isSuccess: isCreateIndeedFlexLongTermAssignmentSuccess,
    error: createIndeedFlexLongTermAssignmentError,
    reset: resetCreateIndeedFlexLongTermAssignment,
  } = useCreateIndeedFlexLongTermAssignmentMutation()

  const value: IndeedFlexShiftActionsContextState = {
    state: {
      createIndeedFlexShiftMutationState: {
        isPending: isCreateIndeedFlexShiftPending,
        isError: isCreateIndeedFlexShiftError,
        isSuccess: isCreateIndeedFlexShiftSuccess,
        errorMessage: parseErrorMessage(createIndeedFlexShiftError),
      },
      createIndeedFlexPastShiftMutationState: {
        isPending: isCreateIndeedFlexPastShiftPending,
        isError: isCreateIndeedFlexPastShiftError,
        isSuccess: isCreateIndeedFlexPastShiftSuccess,
        errorMessage: parseErrorMessage(createIndeedFlexPastShiftError),
      },
      createIndeedFlexLongTermAssignmentMutationState: {
        isPending: isCreateIndeedFlexLongTermAssignmentPending,
        isError: isCreateIndeedFlexLongTermAssignmentError,
        isSuccess: isCreateIndeedFlexLongTermAssignmentSuccess,
        errorMessage: parseErrorMessage(
          createIndeedFlexLongTermAssignmentError
        ),
      },
    },
    actions: {
      createIndeedFlexShift,
      resetCreateIndeedFlexShift,
      createIndeedFlexPastShift,
      resetCreateIndeedFlexPastShift,
      createIndeedFlexLongTermAssignment,
      resetCreateIndeedFlexLongTermAssignment,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useIndeedFlexShiftActionsContextValue }
